import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Upload, FileText, Trash2, Loader2, Image as ImageIcon, ExternalLink } from "lucide-react";

const CATEGORIES = [
  { key: "bolletta", label: "Bolletta", accept: "image/*,application/pdf", icon: FileText },
];

function Thumb({ att, token, onDelete }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const isImage = (att.content_type || "").startsWith("image/");
  const openUrl = `${API}/files/${att.storage_path}?auth=${token}`;

  useEffect(() => {
    let url;
    if (isImage) {
      api.get(`/files/${att.storage_path}`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setBlobUrl(url); })
        .catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [att.storage_path, isImage]);

  return (
    <div className="group relative border border-border rounded overflow-hidden bg-secondary/30" data-testid={`attachment-${att.id}`}>
      <a href={openUrl} target="_blank" rel="noopener noreferrer" className="block">
        {isImage ? (
          blobUrl ? (
            <img src={blobUrl} alt={att.original_filename} className="h-28 w-full object-cover" />
          ) : (
            <div className="h-28 w-full flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          )
        ) : (
          <div className="h-28 w-full flex flex-col items-center justify-center gap-1.5 text-muted-foreground">
            <FileText className="h-7 w-7" strokeWidth={1.5} />
            <span className="text-xs flex items-center gap-1">Apri PDF <ExternalLink className="h-3 w-3" /></span>
          </div>
        )}
      </a>
      <div className="flex items-center justify-between px-2 py-1.5 border-t border-border">
        <span className="text-xs truncate text-muted-foreground" title={att.original_filename}>{att.original_filename}</span>
        <button onClick={() => onDelete(att.id)} data-testid={`delete-attachment-${att.id}`} className="text-muted-foreground hover:text-destructive shrink-0 ml-1">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function LeadAttachments({ lead, onUpdate }) {
  const [uploading, setUploading] = useState(null);
  const inputs = useRef({});
  const token = localStorage.getItem("token");
  const attachments = lead.attachments || [];

  const upload = async (category, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploading(category);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("file", file);
      const { data } = await api.post(`/leads/${lead.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate(data);
      toast.success("File caricato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(null);
      if (inputs.current[category]) inputs.current[category].value = "";
    }
  };

  const remove = async (attId) => {
    try {
      const { data } = await api.delete(`/leads/${lead.id}/attachments/${attId}`);
      onUpdate(data);
      toast.success("Allegato eliminato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="rounded border-border p-5" data-testid="lead-attachments">
      <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><ImageIcon className="h-4 w-4 text-primary" strokeWidth={1.5} /> Allegati</h2>
      <p className="text-xs text-muted-foreground mb-4">Carica la bolletta per avviare l'analisi dei consumi.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        {CATEGORIES.map((cat) => {
          const items = attachments.filter((a) => a.category === cat.key);
          return (
            <div key={cat.key} className="space-y-2">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <cat.icon className="h-3.5 w-3.5" strokeWidth={1.5} /> {cat.label}
              </div>
              <input
                ref={(el) => (inputs.current[cat.key] = el)}
                type="file"
                accept={cat.accept}
                className="hidden"
                data-testid={`file-input-${cat.key}`}
                onChange={(e) => upload(cat.key, e.target.files)}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => inputs.current[cat.key]?.click()}
                disabled={uploading === cat.key}
                data-testid={`upload-${cat.key}-btn`}
                className="w-full border-dashed"
              >
                {uploading === cat.key ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Carica file
              </Button>
              {items.length > 0 && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {items.map((att) => <Thumb key={att.id} att={att} token={token} onDelete={remove} />)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
